import { useEffect, type RefObject } from 'react';

/**
 * Setzt den Bogen an den Anfang, sobald Prüfling oder Station wechseln.
 *
 * ## Warum das nötig ist
 *
 * ⚠️ **Der Bogen wird beim Wechsel nicht neu gemountet.** `Bogen.tsx` tauscht nur seine
 * Aufgabenliste aus; das scrollende Element bleibt dasselbe und behält damit seine
 * Scrollstellung. Der Prüfer bekäme den nächsten Prüfling mitten im Bogen vorgesetzt — und
 * hielte die oberen, nie gesehenen Aufgaben für nicht vorhanden. Bei einer Station mit
 * kürzerem Bogen klemmt der Browser den Wert zwar auf das neue Ende, aber eben auf *dessen
 * Ende*, nicht auf den Anfang.
 *
 * ⚠️ **Der Wechsel-Abzug verschärft es.** `captureBogen` scrollt den Bogen zum Aufnehmen
 * durch und stellt am Ende die Stellung des Prüfers wieder her (`element.scrollTop = vorher`).
 * Ohne diese Rücksetzung wäre genau diese wiederhergestellte, zum **alten** Prüfling gehörende
 * Stellung das, was der neue Bogen erbt.
 *
 * ## Warum ein Schlüssel und kein Zeichenlauf
 *
 * ⚠️ **Der Gegenfall wiegt genauso schwer.** Jedes Häkchen zeichnet den Bogen neu. Liefe die
 * Rücksetzung bei jedem Lauf, risse sie den Bogen nach oben, sobald der Prüfer an der dritten
 * Aufgabe etwas anhakt — Bewerten wäre unmöglich. Deshalb hängt der Effekt allein am
 * Schlüssel aus Station und Prüfling.
 *
 * Aus demselben Grund stört er den Abzug nicht: der ändert nur `shotStamp`, nicht den
 * Schlüssel.
 *
 * ## Warum hier und nicht an den Wechselpfaden im Store
 *
 * Es gibt mehr Wege zu einem anderen Bogen, als der Abzug kennt — der Startablauf, der
 * Wiedereinstieg, das Vergessen einer Prüfung. Ein Effekt am Schlüssel deckt sie alle ab; fünf
 * Aufrufe im Store deckten die ab, an die jemand gedacht hat.
 */
export function useNachObenBeiWechsel(
  ref: RefObject<HTMLElement | null>,
  schluessel: string,
): void {
  useEffect(() => {
    // Fehlt das Element (leerer Bogen: `Bogen.tsx` steigt vor dem `div` aus), gibt es nichts
    // zu rollen — und beim nächsten Mal läuft der Effekt ohnehin erneut.
    const element = ref.current;
    if (element) element.scrollTop = 0;
  }, [ref, schluessel]);
}
